let cartEventPromise;

const getCartEvent = () => {
  cartEventPromise ??=
    import('@shopify/events')
      .then(
        ({ CartLinesUpdateEvent }) =>
          CartLinesUpdateEvent
      )
      .catch(() => null);

  return cartEventPromise;
};

const nextPaint = () =>
  new Promise(resolve => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
  });

const openCart = () => {
  const trigger =
    document.querySelector(
      '[data-testid="cart-drawer-trigger"][aria-controls="cart-drawer"]'
    ) ||
    document.querySelector(
      '[data-testid="cart-drawer-trigger"]'
    );

  if (
    trigger &&
    trigger.getAttribute('aria-expanded') !== 'true'
  ) {
    trigger.click();
  }
};

class ZptProductBundles extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;

    this.abortController =
      new AbortController();

    const { signal } =
      this.abortController;

    this.config =
      JSON.parse(
        this.querySelector(
          '[data-zpt-bundle-config]'
        )?.textContent || '{}'
      );

    this.tiers = [
      ...this.querySelectorAll(
        '[data-bundle-tier]'
      )
    ];

    this.submit =
      this.querySelector(
        '[data-bundle-submit]'
      );

    this.submitText =
      this.querySelector(
        '[data-bundle-button-text]'
      );

    this.initTiers();
    this.syncAvailability();

    this.addEventListener(
      'change',
      event =>
        this.handleChange(event),
      { signal }
    );

    this.submit?.addEventListener(
      'click',
      event =>
        this.handleSubmit(event),
      { signal }
    );
  }

  disconnectedCallback() {
    this.abortController?.abort();

    clearTimeout(
      this.resetTimer
    );

    this.initialized = false;
  }

  formatMoney(cents) {
    const value =
      Math.round(
        Number(cents) || 0
      );

    if (
      window.Shopify?.formatMoney
    ) {
      return window.Shopify.formatMoney(
        value,
        this.config.moneyFormat
      );
    }

    return new Intl.NumberFormat(
      this.config.locale,
      {
        style: 'currency',
        currency:
          this.config.currencyCode
      }
    ).format(value / 100);
  }

  findVariant(row) {
    const selects = [
      ...row.querySelectorAll(
        '.zpt-product-bundles__variant-select'
      )
    ];

    if (!selects.length) {
      return this.config.variants.find(
        variant =>
          Number(variant.id) ===
          Number(
            this.config
              .fallbackVariantId
          )
      );
    }

    const options =
      selects.map(
        select => select.value
      );

    return this.config.variants.find(
      variant =>
        variant.options.every(
          (option, index) =>
            String(option) ===
            String(options[index])
        )
    );
  }

  initTiers() {
    const selected =
      this.tiers.find(
        tier =>
          tier.dataset
            .defaultSelected ===
          'true'
      ) ||
      this.tiers.find(
        tier =>
          tier.querySelector(
            '.zpt-product-bundles__radio-input'
          )?.checked
      ) ||
      this.tiers[0];

    if (selected) {
      this.selectTier(selected);
    }

    this.tiers.forEach(
      tier =>
        this.updateTierPrice(tier)
    );
  }

  handleChange(event) {
    if (
      !(event.target instanceof Element)
    ) {
      return;
    }

    const select =
      event.target.closest(
        '.zpt-product-bundles__variant-select'
      );

    if (select) {
      const tier =
        select.closest(
          '[data-bundle-tier]'
        );

      if (!tier) return;

      this.selectTier(tier);
      this.updateTierPrice(tier);
      this.syncAvailability();

      return;
    }

    const radio =
      event.target.closest(
        '.zpt-product-bundles__radio-input'
      );

    if (
      radio &&
      radio.checked
    ) {
      this.selectTier(
        radio.closest(
          '[data-bundle-tier]'
        )
      );
      this.syncAvailability();
    }
  }

  selectTier(selectedTier) {
    if (!selectedTier) return;

    this.tiers.forEach(tier => {
      const selected =
        tier === selectedTier;

      tier.classList.toggle(
        'is-active',
        selected
      );

      const radio =
        tier.querySelector(
          '.zpt-product-bundles__radio-input'
        );

      if (radio) {
        radio.checked = selected;
      }
    });
  }

  syncAvailability() {
    const tier = this.querySelector(
      '[data-bundle-tier].is-active'
    );

    const rows = tier
      ? [...tier.querySelectorAll('[data-bundle-item]')]
      : [];

    const variants = rows.length
      ? rows.map(row => this.findVariant(row))
      : [this.config.variants.find(
          variant => Number(variant.id) === Number(this.config.fallbackVariantId)
        )];

    const hasVariants = Boolean(
      tier && variants.length && variants.every(Boolean)
    );
    const available = hasVariants && variants.every(variant => variant.available);

    if (!this.submit) return available;

    if (this.isSubmitting) {
      this.submit.setAttribute('aria-busy', 'true');
    } else {
      this.submit.removeAttribute('aria-busy');
    }

    this.submit.disabled = this.isSubmitting || !available;
    this.submit.dataset.available = String(available);

    if (this.submitText && !this.isSubmitting && !this.isAdded) {
      this.submitText.textContent = available
        ? this.config.buttonText || 'Add to cart'
        : hasVariants
          ? this.config.soldOutText || 'Sold out'
          : this.config.unavailableText || 'Unavailable';
    }

    return available;
  }

  updateTierPrice(tier) {
    const qty =
      Number(
        tier.dataset.qty
      ) || 1;

    const rows = [
      ...tier.querySelectorAll(
        '[data-bundle-item]'
      )
    ];

    let regular = 0;
    let nativeCompare = 0;

    if (rows.length) {
      for (const row of rows) {
        const variant =
          this.findVariant(row);

        if (!variant) return;

        const price =
          Number(
            variant.price
          ) || 0;

        const compare =
          Number(
            variant
              .compare_at_price
          ) || 0;

        regular += price;

        nativeCompare +=
          compare > price
            ? compare
            : price;
      }
    } else {
      const variant =
        this.config.variants.find(
          item =>
            Number(item.id) ===
            Number(
              this.config
                .fallbackVariantId
            )
        ) ||
        this.config.variants[0];

      if (!variant) return;

      const price =
        Number(
          variant.price
        ) || 0;

      const compare =
        Number(
          variant
            .compare_at_price
        ) || 0;

      regular =
        price * qty;

      nativeCompare =
        qty === 1 &&
        compare > price
          ? compare
          : regular;
    }

    const type =
      tier.dataset
        .discountType ||
      'none';

    const value =
      Math.max(
        0,
        Number(
          tier.dataset
            .discountValue
        ) || 0
      );

    const compare =
      qty === 1 && type === 'none'
        ? nativeCompare
        : regular;

    let current =
      qty === 1 && type === 'none'
        ? regular
        : compare;

    if (type === 'percentage') {
      const percentage = Math.min(value, 100);
      current = rows.length
        ? rows.reduce((total, row) => {
            const variant = this.findVariant(row);
            if (!variant) return total;

            const price = Number(variant.price) || 0;
            const discount = Math.floor(price * percentage / 100);
            return total + price - discount;
          }, 0)
        : Math.round(regular / qty) * qty -
          Math.floor((Math.round(regular / qty) * percentage) / 100) * qty;
    } else if (type === 'fixed_price') {
      current = value * 100 * qty;
    } else if (type === 'fixed_amount') {
      current -= value * 100;
    }

    current = Math.min(compare, Math.max(0, current));

    const savings =
      Math.max(
        0,
        compare - current
      );

    const currentEl =
      tier.querySelector(
        '[data-bundle-current-price]'
      );

    const compareEl =
      tier.querySelector(
        '[data-bundle-compare-price]'
      );

    const saveEl =
      tier.querySelector(
        '[data-bundle-save]'
      );

    if (currentEl) {
      currentEl.textContent =
        this.formatMoney(
          current
        );
    }

    if (compareEl) {
      compareEl.hidden =
        compare <= current;

      if (!compareEl.hidden) {
        compareEl.textContent =
          this.formatMoney(
            compare
          );
      }
    }

    if (saveEl) {
      const showSavings =
        savings > 0 &&
        (qty > 1 || (type !== 'none' && value > 0));

      saveEl.hidden = !showSavings;

      if (!saveEl.hidden) {
        saveEl.textContent =
          type === 'percentage'
            ? `SAVE ${Math.min(value, 100)}% (${this.formatMoney(savings)})`
            : `SAVE ${this.formatMoney(savings)}`;
      }
    }
  }

  buildItems(tier) {
    const items =
      new Map();

    const add = (
      id,
      quantity = 1,
      offerItem = true
    ) => {
      const variantId =
        Number(id);

      const itemQuantity =
        Number(quantity);

      if (
        !variantId ||
        itemQuantity < 1
      ) {
        return;
      }

      const key = `${variantId}:${offerItem ? 'offer' : 'extra'}`;
      const current = items.get(key);
      items.set(key, {
        id: variantId,
        quantity: (current?.quantity || 0) + itemQuantity,
        offerItem,
      });
    };

    const rows = [
      ...tier.querySelectorAll(
        '[data-bundle-item]'
      )
    ];

    if (rows.length) {
      for (const row of rows) {
        const variant =
          this.findVariant(row);

        if (!variant) {
          throw new Error(
            'This option combination is unavailable.'
          );
        }

        if (!variant.available) {
          throw new Error(
            'One of the selected variants is sold out.'
          );
        }

        add(
          variant.id
        );
      }
    } else {
      const variant =
        this.config.variants.find(
          item =>
            Number(item.id) ===
            Number(
              this.config
                .fallbackVariantId
            )
        );

      if (!variant?.available) {
        throw new Error(
          'This product is currently sold out.'
        );
      }

      add(
        variant.id,
        Number(
          tier.dataset.qty
        ) || 1
      );
    }

    this.querySelectorAll(
      '[data-bundle-extra]:checked'
    ).forEach(extra => {
      add(
        extra.dataset.variantId,
        1,
        false
      );
    });

    return [...items.values()].map(item => ({
      id: item.id,
      quantity: item.quantity,
    }));
  }

  getSectionIds() {
    const ids = [
      'zpt-cart-drawer-section',
      ...[
        ...document.querySelectorAll(
          'cart-items-component'
        )
      ]
        .map(
          item =>
            item.dataset.sectionId
        )
        .filter(Boolean)
    ];

    return [
      ...new Set(ids)
    ].slice(0, 5);
  }

  async fetchCart() {
    const root =
      window.Shopify?.routes
        ?.root ||
      '/';

    const response =
      await fetch(
        `${root}cart.js`,
        {
          method: 'GET',

          headers: {
            Accept:
              'application/json'
          },

          credentials:
            'same-origin',

          cache:
            'no-store'
        }
      );

    if (!response.ok) {
      throw new Error(
        'Could not refresh the cart.'
      );
    }

    return response.json();
  }

  async addSingleItemToCart(variantId) {
    // The sticky quick-add represents one unit, even when a multi-item bundle is selected.
    const variant = this.config.variants.find(
      item => Number(item.id) === Number(variantId)
    );

    if (!variant) {
      throw new Error(
        'This option combination is unavailable.'
      );
    }

    if (!variant.available) {
      throw new Error(
        'This selected variant is sold out.'
      );
    }

    return this.addToCart([
      {
        id: variant.id,
        quantity: 1,
      },
    ]);
  }

  async addToCart(items) {
    if (
      window.ZptStorefrontCart
        ?.syncing
    ) {
      throw new Error(
        'Your cart is still loading. Please try again.'
      );
    }

    const CartLinesUpdateEvent =
      await getCartEvent();

    if (!CartLinesUpdateEvent) {
      throw new Error(
        'Cart synchronization is unavailable.'
      );
    }

    const sectionIds =
      this.getSectionIds();

    const root =
      window.Shopify?.routes
        ?.root ||
      '/';

    const deferred =
      CartLinesUpdateEvent
        .createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',

        context:
          'product',

        lines:
          items.map(
            item => ({
              merchandiseId:
                String(item.id),

              quantity:
                item.quantity
            })
          ),

        promise:
          deferred.promise
      })
    );

    try {
      const response =
        await fetch(
          `${root}cart/add.js`,
          {
            method: 'POST',

            headers: {
              'Content-Type':
                'application/json',

              Accept:
                'application/json'
            },

            credentials:
              'same-origin',

            body:
              JSON.stringify({
                items,

                sections:
                  sectionIds.join(',')
              })
          }
        );

      const result =
        await response.json();

      if (
        !response.ok ||
        result?.status
      ) {
        throw new Error(
          result?.description ||
          result?.message ||
          'Could not add this bundle.'
        );
      }

      const cart =
        await this.fetchCart();

      deferred.resolve({
        cart:
          CartLinesUpdateEvent
            .createCartFromAjaxResponse(
              cart
            ),

        detail: {
          items:
            cart.items,

          sections:
            result.sections || {},

          source:
            'zpt-product-bundles',

          sourceId:
            this.id,

          itemCount:
            cart.item_count,

          productId:
            this.config
              .productId,

          didError:
            false
        }
      });

      window
        .ZptStorefrontCart
        ?.writeSnapshot?.(
          cart
        );

      window.dispatchEvent(
        new CustomEvent(
          'zpt:cart-mutated',
          {
            detail: {
              cart
            }
          }
        )
      );

      await nextPaint();

      openCart();

    } catch (error) {
      deferred.reject(error);

      throw error;
    }
  }

  setButton(
    text,
    disabled
  ) {
    if (this.submit) {
      this.submit.disabled =
        disabled;

      if (this.isSubmitting) {
        this.submit.setAttribute('aria-busy', 'true');
      } else {
        this.submit.removeAttribute('aria-busy');
      }
    }

    if (this.submitText) {
      this.submitText
        .textContent =
        text;
    }
  }

  async handleSubmit(event) {
    event.preventDefault();

    if (
      !this.submit ||
      this.submit.disabled
    ) {
      return;
    }

    const tier =
      this.querySelector(
        '[data-bundle-tier].is-active'
      );

    if (!tier) return;

    this.isSubmitting = true;
    this.setButton(
      'ADDING...',
      true
    );

    try {
      const items =
        this.buildItems(
          tier
        );

      if (!items.length) {
        throw new Error(
          'No items were selected.'
        );
      }

      await this.addToCart(
        items
      );

      this.isSubmitting = false;
      this.isAdded = true;
      this.setButton(
        'ADDED',
        true
      );

      clearTimeout(
        this.resetTimer
      );

      this.resetTimer =
        setTimeout(
          () => {
            this.isAdded = false;
            this.syncAvailability();
          },
          900
        );

    } catch (error) {
      this.isSubmitting = false;
      console.error(
        'ZPT Product Bundles:',
        error
      );

      alert(
        error.message ||
        'Could not add this bundle.'
      );

      this.syncAvailability();
    }
  }
}

if (
  !customElements.get(
    'zpt-product-bundles'
  )
) {
  customElements.define(
    'zpt-product-bundles',
    ZptProductBundles
  );
}
